import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../../api';

const Temple = () => {
    const [deities, setDeities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadDeities = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await fetch(`${API_URL}/temple/deities`, {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error(`Failed to load deities: ${response.status}`);
                }

                const data = await response.json();
                setDeities((data.deities || []).filter(d => !d.requiresEmpowerment));
            } catch (err) {
                console.error('Error loading deities:', err);
                setError('Could not load temple deities. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        loadDeities();
    }, []);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white flex items-center justify-center">
                <div className="text-2xl">Loading Virtual Temple...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white flex items-center justify-center">
                <div className="text-center space-y-4">
                    <p className="text-red-400 text-lg">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-lg font-semibold transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white">
            {/* Hero Section */}
            <div className="relative px-6 py-16 text-center">
                <h1 className="text-5xl font-bold mb-6">Virtual Temple</h1>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                    Step into our sacred digital space where ancient wisdom meets modern technology.
                    Experience immersive practices with synchronized visuals, audio, and guidance.
                </p>
            </div>

            {/* Deities Grid */}
            <div className="max-w-6xl mx-auto px-6 pb-16">
                <h2 className="text-3xl font-bold text-center mb-12">Available Deities</h2>

                {deities.length === 0 ? (
                    <div className="text-center text-gray-400 py-16">
                        <p className="text-xl">No deities available at this time.</p>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {deities.map((deity) => (
                            <div
                                key={deity.id}
                                className="bg-gray-800 bg-opacity-90 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
                            >
                                {/* Deity Image */}
                                <div className="h-48 bg-gradient-to-b from-purple-600 to-purple-800 flex items-center justify-center overflow-hidden">
                                    {deity.image ? (
                                        <img
                                            src={deity.image}
                                            alt={deity.name}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <div className="text-6xl opacity-50">&#x1F549;</div>
                                    )}
                                </div>

                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="text-2xl font-bold">{deity.name}</h3>
                                        {deity.requiresEmpowerment && (
                                            <span className="bg-yellow-600 bg-opacity-20 text-yellow-300 px-2 py-1 rounded-full text-xs font-medium">
                                                Empowerment Required
                                            </span>
                                        )}
                                    </div>

                                    <p className="text-gray-300 mb-4">{deity.description}</p>

                                    {/* Action Button */}
                                    <Link
                                        to={`/temple/${deity.id}`}
                                        className="w-full bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg font-semibold transition-colors text-center block"
                                    >
                                        Enter Sacred Space
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Information Section */}
                <div className="mt-16 bg-gray-800 bg-opacity-50 rounded-lg p-8">
                    <h3 className="text-2xl font-bold mb-4">About Virtual Temple Practices</h3>
                    <div className="grid md:grid-cols-2 gap-8">
                        <div>
                            <h4 className="text-lg font-semibold mb-2 text-blue-300">Open Practices</h4>
                            <p className="text-gray-300">
                                These practices are available to all practitioners. They include basic mantras,
                                refuge prayers, and preliminary visualizations that form the foundation of Buddhist practice.
                            </p>
                        </div>
                        <div>
                            <h4 className="text-lg font-semibold mb-2 text-amber-300">Protected Practices</h4>
                            <p className="text-gray-300">
                                These advanced practices require proper empowerment and transmission from a qualified teacher.
                                Access is granted after verification of your lineage connection and empowerment status.
                            </p>
                        </div>
                    </div>

                    <div className="mt-6 p-4 bg-blue-900 bg-opacity-30 rounded-lg">
                        <p className="text-blue-200 text-sm">
                            <strong>Note:</strong> The Virtual Temple enhances your practice but does not replace
                            traditional teacher-student relationships or formal empowerments. Always maintain connection
                            with your root teacher and lineage.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Temple;
